import React from "react"
import { useNavigate } from "@tanstack/react-router"
import { Building2, ExternalLink, Package } from "lucide-react"
import type { ProductSearchResult, ExternalProductSearchResult } from "@/lib/api/search.api"
import { useCompanyDetails } from "@/hooks/seller/useCompanyDetails"
import { cn } from "@/lib/utils"

export interface ProductSearchCardProps {
  product: ProductSearchResult | ExternalProductSearchResult
  companyName?: string
  isActiveTarget?: boolean
  onRequestQuote: () => void
}

export function ProductSearchCard({ product, companyName, isActiveTarget, onRequestQuote }: ProductSearchCardProps) {
  const navigate = useNavigate()

  const isExternal = 'productUrl' in product || 'externalSellerId' in product || 'companyUrl' in product || 'productImageUrl' in product
  const extProduct = isExternal ? (product as ExternalProductSearchResult) : null
  const intProduct = !isExternal ? (product as ProductSearchResult) : null

  const rawProduct = product as unknown as Record<string, unknown>

  const targetCompanyId = !isExternal
    ? (intProduct?.companyId || (typeof rawProduct.companyId === "string" ? rawProduct.companyId : undefined) || intProduct?.company?.id)
    : undefined

  const existingCompanyName =
    companyName ||
    intProduct?.companyName ||
    intProduct?.company?.name ||
    (typeof rawProduct.companyName === "string" ? rawProduct.companyName : undefined)

  const { data: companyData, isLoading: isLoadingCompany } = useCompanyDetails(
    targetCompanyId,
    !isExternal && !!targetCompanyId && !existingCompanyName
  )

  const displayName = isExternal ? product.productName : (intProduct?.name || product.productName)

  const resolvedCompanyName =
    existingCompanyName ||
    companyData?.name ||
    extProduct?.companyName

  const handleCompanyClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!targetCompanyId) return

    const currentReturnUrl = typeof window !== "undefined"
      ? (window.location.pathname + window.location.search)
      : undefined

    const currentSearchParams = typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : null
    const currentCategory = currentSearchParams?.get("category") || undefined

    navigate({
      to: "/explore/companies/$companyId",
      params: { companyId: targetCompanyId },
      search: {
        companyType: "internal",
        companyName: resolvedCompanyName || undefined,
        category: currentCategory,
        returnUrl: currentReturnUrl,
      },
    })
  }

  const rawImages = Array.isArray(rawProduct.images) ? (rawProduct.images as { url?: string }[]) : undefined
  const rawImageUrls = Array.isArray(rawProduct.imageUrls) ? (rawProduct.imageUrls as { url?: string }[]) : undefined
  const rawImageUrl = typeof rawProduct.imageUrl === "string" ? rawProduct.imageUrl : null

  const displayImageUrl =
    extProduct?.productImageUrl ||
    intProduct?.imageUrl ||
    (intProduct?.imageUrls && intProduct.imageUrls.length > 0 ? intProduct.imageUrls[0].url : null) ||
    rawImageUrl ||
    rawImageUrls?.[0]?.url ||
    rawImages?.[0]?.url ||
    null

  return (
    <div
      onClick={onRequestQuote}
      className={cn(
        "group relative overflow-hidden flex flex-col justify-between h-full bg-white rounded-xl p-3.5 border transition-all duration-300 cursor-pointer",
        isActiveTarget
          ? "border-[#be001e] shadow-sm shadow-[#be001e]/5"
          : "border-slate-200 hover:border-[#be001e]/30 hover:shadow-[0_8px_24px_rgba(230,32,47,0.06)] hover:-translate-y-1 hover:scale-[1.01]"
      )}
    >
      {/* Top Accent Glow */}
      <div className={cn(
        "absolute top-0 left-0 right-0 h-[1.5px] group-hover:h-[2.5px] rounded-t-xl opacity-0 group-hover:opacity-100 transition-all duration-300",
        isExternal ? "bg-gradient-to-r from-amber-500 to-[#be001e]" : "bg-gradient-to-r from-[#be001e] to-[#e6202f]"
      )} />

      <div className="space-y-2.5">
        {/* Product Thumbnail Image */}
        <div className="relative w-full h-36 rounded-lg overflow-hidden border border-slate-100 bg-gradient-to-br from-slate-50 to-slate-100/50 flex items-center justify-center shrink-0">
          {displayImageUrl ? (
            <img 
              src={displayImageUrl} 
              alt={displayName}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              onError={(e) => {
                (e.target as HTMLElement).style.display = 'none';
                const parent = (e.target as HTMLElement).parentElement;
                if (parent) {
                  const fallbackDiv = parent.querySelector('.img-fallback-placeholder');
                  if (fallbackDiv) {
                    fallbackDiv.classList.remove('hidden');
                  }
                }
              }}
            />
          ) : null}
          
          <div className={cn(
            "img-fallback-placeholder flex flex-col items-center justify-center gap-1.5 absolute inset-0 text-slate-400 group-hover:scale-105 transition-transform duration-300",
            displayImageUrl ? "hidden" : ""
          )}>
            <Package className="size-8 text-slate-300" />
            <span className="text-[10px] text-slate-400/80 font-medium">No Image Available</span>
          </div>
        </div>

        {/* Title & SKU */}
        <div className="space-y-0.5">
          <h3 className="text-[13.5px] font-bold text-slate-800 leading-snug group-hover:text-[#be001e] transition-colors duration-200 line-clamp-2" title={displayName}>
            {displayName}
          </h3>
        </div>

        {/* Company Name */}
        {(resolvedCompanyName || isLoadingCompany) && (
          <div
            onClick={!isExternal && targetCompanyId ? handleCompanyClick : undefined}
            className={cn(
              "flex items-center justify-between gap-1 text-[10px] text-slate-500 font-semibold bg-slate-50/50 px-2 py-0.5 rounded-md border border-slate-100/60 max-w-full transition-colors",
              !isExternal && targetCompanyId
                ? "hover:bg-red-50/50 hover:border-[#be001e]/30 cursor-pointer group/comp"
                : "hover:bg-slate-100/80"
            )}
            title={!isExternal && targetCompanyId ? `View ${resolvedCompanyName || "company"} details` : undefined}
          >
            <div className="flex items-center gap-1 min-w-0">
              <Building2 className={cn(
                "size-3 text-slate-400 shrink-0 transition-colors",
                !isExternal && targetCompanyId && "group-hover/comp:text-[#be001e]"
              )} />
              {isLoadingCompany && !resolvedCompanyName ? (
                <span className="w-16 h-3 bg-slate-200/60 animate-pulse rounded inline-block" />
              ) : (
                <span className={cn(
                  "truncate",
                  !isExternal && targetCompanyId && "group-hover/comp:text-[#be001e] group-hover"
                )}>
                  by {resolvedCompanyName}
                </span>
              )}
            </div>
            {extProduct?.companyUrl && (
              <a
                href={extProduct.companyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#be001e] hover:underline flex items-center gap-0.5 shrink-0 ml-1"
                title="Visit Company Page"
              >
                <ExternalLink className="size-2.5" />
              </a>
            )}
          </div>
        )}
      </div>

      {/* Footer Details Button */}
      <div className="mt-3.5 pt-2.5 border-t border-slate-100/60 flex gap-2">
        <button
          onClick={(e) => {
            e.stopPropagation()
            onRequestQuote()
          }}
          className="flex-1 h-8 bg-gradient-to-r from-[#be001e] to-[#e60024] text-white rounded-lg flex items-center justify-center gap-1 text-[10.5px] font-bold shadow-sm shadow-[#be001e]/10"
        >
          Request Quote
        </button>
      </div>
    </div>
  )
}
